/**
 * LoginScreen — overlay que bloquea la app hasta que el alumno se autentica.
 *
 * Solo login: el registro abierto fue removido porque cualquiera con el link
 * podía crearse cuenta (y dejarse rol teacher). Las cuentas las crea el admin
 * desde el servidor vía `/auth/register` con JWT. Si se necesita self-signup
 * controlado en el futuro, habilitar un flag del backend y devolver el tab.
 */

import { authService, type PublicUser } from './AuthService';

const OVERLAY_ID = 'cgt-auth-overlay';

export function mountLoginScreen(onAuthenticated: (user: PublicUser) => void): void {
  if (authService.isAuthenticated) {
    onAuthenticated(authService.currentUser!);
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.innerHTML = `
    <div class="cgt-auth-card">
      <div class="cgt-auth-header">
        <div class="cgt-auth-brand">
          <img class="cgt-auth-logo" src="/logo.svg" alt="Dr Newen IA" width="52" height="52" />
          <div class="cgt-auth-brand-text">
            <div class="cgt-auth-eyebrow">Clinical Triage System</div>
            <h1 class="cgt-auth-title">Dr Newen <span class="cgt-auth-ia">IA</span></h1>
          </div>
        </div>
        <p class="cgt-auth-sub">Entrená tu criterio clínico con casos reales, IA y hand tracking.</p>
      </div>

      <form class="cgt-auth-form" data-form="login" novalidate>
        <label class="cgt-auth-field">
          <span>Email</span>
          <input type="email" name="email" autocomplete="email" required />
        </label>
        <label class="cgt-auth-field">
          <span>Contraseña</span>
          <input type="password" name="password" autocomplete="current-password" required minlength="8" />
        </label>
        <button type="submit" class="cgt-auth-submit">Entrar</button>
      </form>

      <div class="cgt-auth-error" data-error hidden></div>
      <p class="cgt-auth-footer">Acceso con cuenta provista por el profesor. Solo para fines educativos.</p>
    </div>
  `;

  injectStyles();
  document.body.appendChild(overlay);
  // Oculta el landing con casos de demo mientras el login está activo:
  // evita el "leak" visual de datos clínicos y fija aria-hidden para
  // que los lectores de pantalla no los naveguen pre-auth.
  document.body.classList.add('cgt-login-active');
  const landing = document.getElementById('landing');
  if (landing) {
    landing.setAttribute('aria-hidden', 'true');
  }

  const err = overlay.querySelector<HTMLElement>('[data-error]')!;
  const loginForm = overlay.querySelector<HTMLFormElement>('[data-form="login"]')!;

  const showError = (msg: string): void => {
    err.textContent = msg;
    err.hidden = false;
  };

  const finish = (user: PublicUser): void => {
    overlay.classList.add('cgt-auth-fade-out');
    setTimeout(() => {
      overlay.remove();
      document.body.classList.remove('cgt-login-active');
      const l = document.getElementById('landing');
      if (l) l.removeAttribute('aria-hidden');
    }, 400);
    onAuthenticated(user);
  };

  loginForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    err.hidden = true;
    const data = new FormData(loginForm);
    try {
      const user = await authService.login(
        String(data.get('email') ?? ''),
        String(data.get('password') ?? ''),
      );
      finish(user);
    } catch (ex) {
      showError(ex instanceof Error ? ex.message : 'Error al iniciar sesión');
    }
  });

}

function injectStyles(): void {
  if (document.getElementById('cgt-auth-styles')) return;
  const style = document.createElement('style');
  style.id = 'cgt-auth-styles';
  style.textContent = `
    #${OVERLAY_ID} {
      position: fixed; inset: 0; z-index: 20000;
      display: flex; align-items: center; justify-content: center;
      background: radial-gradient(circle at 30% 30%, #0f172a 0%, #000 80%);
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      color: #f5f5f7;
      transition: opacity 0.4s ease;
    }
    #${OVERLAY_ID}.cgt-auth-fade-out { opacity: 0; pointer-events: none; }
    .cgt-auth-card {
      width: min(420px, 92vw);
      background: #0a0a0a;
      border: 0.5px solid rgba(255,255,255,0.08);
      border-radius: 20px;
      padding: 36px 32px 28px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.4);
    }
    #${OVERLAY_ID} .cgt-auth-header { margin-bottom: 24px; }
    #${OVERLAY_ID} .cgt-auth-brand {
      display: flex; align-items: center; gap: 14px; margin-bottom: 10px;
    }
    #${OVERLAY_ID} .cgt-auth-logo {
      width: 52px; height: 52px; border-radius: 14px;
      display: block; flex-shrink: 0;
      box-shadow: 0 4px 18px rgba(41,151,255,0.35);
    }
    #${OVERLAY_ID} .cgt-auth-brand-text { display: flex; flex-direction: column; gap: 4px; }
    #${OVERLAY_ID} .cgt-auth-eyebrow {
      font-size: 10px; font-weight: 600; letter-spacing: 1.5px;
      color: #2997FF; text-transform: uppercase;
      margin: 0;
    }
    /* Fuerzo color + familia explícitos: el style.css global pone color:var(--text-h)
       y font-family de headings que acá no aplican. */
    #${OVERLAY_ID} .cgt-auth-title {
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      color: #f5f5f7;
      font-size: 28px; font-weight: 800; letter-spacing: -0.6px;
      line-height: 1.1; margin: 0;
    }
    #${OVERLAY_ID} .cgt-auth-ia {
      background: linear-gradient(90deg, #2997FF, #BF5AF2);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    #${OVERLAY_ID} .cgt-auth-sub { font-size: 13px; color: #a1a1a6; margin-top: 12px; line-height: 1.5; }

    .cgt-auth-tabs {
      display: flex; gap: 4px; margin-bottom: 20px;
      background: rgba(255,255,255,0.03);
      border-radius: 10px; padding: 4px;
    }
    .cgt-auth-tab {
      flex: 1; padding: 8px 14px;
      background: transparent; color: #a1a1a6;
      font-size: 13px; font-weight: 500;
      border: none; border-radius: 8px; cursor: pointer;
      transition: all 0.2s;
    }
    .cgt-auth-tab-active { background: rgba(255,255,255,0.08); color: #fff; }

    .cgt-auth-form { display: flex; flex-direction: column; gap: 14px; }
    .cgt-auth-form-hidden { display: none; }

    .cgt-auth-field { display: flex; flex-direction: column; gap: 6px; }
    .cgt-auth-field span { font-size: 12px; color: #a1a1a6; }
    .cgt-auth-field small { font-size: 11px; color: #6e6e73; }
    .cgt-auth-field input, .cgt-auth-field select {
      background: #111; border: 0.5px solid rgba(255,255,255,0.1);
      border-radius: 10px; padding: 11px 14px;
      color: #fff; font-size: 14px; font-family: inherit;
      transition: border-color 0.2s, background 0.2s;
    }
    .cgt-auth-field input:focus, .cgt-auth-field select:focus {
      outline: none; border-color: #2997FF; background: #141414;
    }

    .cgt-auth-submit {
      margin-top: 4px;
      background: #2997FF; color: #fff;
      border: none; border-radius: 10px;
      padding: 12px; font-size: 14px; font-weight: 600;
      cursor: pointer; font-family: inherit;
      transition: background 0.2s, transform 0.1s;
    }
    .cgt-auth-submit:hover { background: #0a84ff; }
    .cgt-auth-submit:active { transform: scale(0.98); }

    .cgt-auth-error {
      margin-top: 14px; padding: 10px 14px;
      background: rgba(255,69,58,0.1);
      border: 0.5px solid rgba(255,69,58,0.3);
      border-radius: 10px;
      color: #FF6961; font-size: 12px;
    }

    .cgt-auth-footer {
      margin-top: 22px; font-size: 11px; color: #6e6e73; text-align: center;
    }
  `;
  document.head.appendChild(style);
}
